import React, { useMemo } from 'react';
import ReactWordcloud from 'react-wordcloud';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entryPrices = {};
  const exitPrices = {};

  records.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entryPrices[symbol]) entryPrices[symbol] = [];
      entryPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity
      });
    } else if (record.action === 'exit') {
      if (!exitPrices[symbol]) exitPrices[symbol] = [];
      exitPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity
      });
    }
  });

  const pairs = [];
  Object.keys(entryPrices).forEach(symbol => {
    const entries = [...entryPrices[symbol]];
    const exits = exitPrices[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty
        });
      }
    });
  });

  return pairs;
};

/**
 * P&L Word Cloud Chart
 * Word size = P&L amount
 * Color = Green (profit) / Red (loss)
 */
const PLWordCloud = ({ records, pairs = [] }) => {
  // Calculate P&L by symbol
  const { words, stockData } = useMemo(() => {
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    if (pairsToUse.length === 0) {
      return { words: [], stockData: {} };
    }

    const symbolPL = {};
    pairsToUse.forEach(pair => {
      const symbol = pair.symbol;
      if (!symbolPL[symbol]) {
        symbolPL[symbol] = { profit: 0, trades: 0, wins: 0, losses: 0 };
      }
      symbolPL[symbol].profit += pair.profit || 0;
      symbolPL[symbol].trades += 1;
      if ((pair.profit || 0) > 0) {
        symbolPL[symbol].wins += 1;
      } else {
        symbolPL[symbol].losses += 1;
      }
    });

    // Convert to word cloud format
    const words = Object.entries(symbolPL).map(([symbol, data]) => ({
      text: symbol,
      value: Math.abs(data.profit),
      profit: data.profit,
      trades: data.trades,
      wins: data.wins,
      losses: data.losses
    }));

    return { words, stockData: symbolPL };
  }, [records, pairs]);

  // Word cloud options
  const options = {
    rotations: 0,
    rotationAngles: [0, 0],
    fontSizes: [24, 80],
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    padding: 8,
    deterministic: true,
    enableTooltip: true,
    spiral: 'rectangular'
  };

  // Callbacks for coloring
  const callbacks = {
    getWordColor: word => word.profit >= 0 ? '#22c55e' : '#ef4444',
    getWordTooltip: word => {
      const isProfit = word.profit >= 0;
      return `${word.text}\nP&L: ${isProfit ? '+' : ''}$${word.profit.toFixed(2)}\nTrades: ${word.trades} (${word.wins}W / ${word.losses}L)\nWin Rate: ${word.trades > 0 ? ((word.wins / word.trades) * 100).toFixed(0) : 0}%`;
    }
  };

  if (words.length === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Word Cloud requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Calculate totals
  const totalProfit = Object.values(stockData).reduce((sum, s) => sum + (s.profit > 0 ? s.profit : 0), 0);
  const totalLoss = Object.values(stockData).reduce((sum, s) => sum + (s.profit < 0 ? Math.abs(s.profit) : 0), 0);
  const netPL = totalProfit - totalLoss;

  return (
    <div style={{ width: '100%' }}>
      {/* Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Stocks</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{words.length}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Profit</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#22c55e' }}>+${totalProfit.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Loss</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>-${totalLoss.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Net P&L</div>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: netPL >= 0 ? '#22c55e' : '#ef4444'
          }}>
            {netPL >= 0 ? '+' : ''}${netPL.toFixed(0)}
          </div>
        </div>
      </div>

      {/* Word Cloud */}
      <div style={{ height: 400, width: '100%' }}>
        <ReactWordcloud
          words={words}
          options={options}
          callbacks={callbacks}
        />
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#666'
      }}>
        <span><span style={{ color: '#22c55e', fontWeight: 'bold' }}>Green</span> = Profit</span>
        <span><span style={{ color: '#ef4444', fontWeight: 'bold' }}>Red</span> = Loss</span>
        <span>Larger text = Higher P&L amount</span>
      </div>
    </div>
  );
};

export default PLWordCloud;
